import React from "react";
import "../DashboardStyles.css";

export default function Chapter7Dashboard() {
  return (
    <div className="chapter chapter7">
      <div className="chapter-content">
        <p className="chapter-eyebrow">Chapter 7</p>
        <h2>Higher Education and Workforce Development</h2>

        <h3>7.1 Introduction</h3>
        <p>
          Higher education in the Nebraska Panhandle operates under conditions
          fundamentally different from metropolitan Nebraska. The region’s two
          primary post-secondary institutions (Chadron State College and Western
          Nebraska Community College) serve a geographically dispersed,
          demographically declining population across eleven counties and nearly
          24,000 square miles, with limited resources and under constant
          demographic pressure. Yet they are not marginal players: they are
          load-bearing pillars of the Panhandle economy, training the nurses,
          welders, diesel technicians, business managers, teachers, and
          agricultural specialists who fill the region’s most critical workforce
          needs, and they are the institutions most capable of bending the
          trajectory of the region’s skills mismatch.
        </p>

        <h3>7.2 Chadron State College</h3>
        <p>
          Chadron State College (CSC), in Dawes County, is the Panhandle’s only
          four-year degree-granting institution and one of three in the Nebraska
          State College System. Founded in 1911, with roughly 2,500 students and
          300–400 faculty and staff, CSC is among the top three employers in
          Dawes County. Its programs in education are especially significant
          given chronically thin regional teacher pipelines, and its business,
          agricultural, and natural-science programs align with the region’s
          economy. CSC’s health is a direct indicator of the region’s capacity
          to develop its own human capital. The most effective response to brain
          drain is deliberate student-employer ties during enrollment —
          internships, cooperative education, employer-sponsored scholarships —
          a priority in Goal 2.
        </p>

        <h3>7.3 Western Nebraska Community College</h3>
        <p>
          Western Nebraska Community College (WNCC), headquartered in
          Scottsbluff with satellite campuses in Sidney, Alliance, and other
          locations, is the Panhandle’s primary provider of technical education,
          workforce training, associate degrees, and continuing education — and
          the most geographically accessible institution for most residents. Its
          healthcare programs supply the pipeline for Regional West and the six
          CAHs; its skilled-trades programs (welding, diesel, HVAC, electrical)
          serve agricultural maintenance, construction, and energy; and its
          precision-agriculture programs are positioned to grow as BEAD enables
          GPS equipment, drone scouting, and digital farm management. WNCC’s
          workforce-development division is the primary mechanism through which
          the employer-linked pipeline strategies in Goal 2 will be implemented.
        </p>

        <h3>7.4 The Brain Drain Challenge</h3>
        <p>
          The departure of college-educated young adults — and the failure of
          most to return — is addressed in every CEDS the Panhandle has produced
          and solved by none. This plan distinguishes what can and cannot be
          influenced. What cannot: young adults will leave for college in urban
          markets with more programs, resources, and proximate jobs. What can:
          the decision to return or remain after completing a degree — driven by
          job availability, wages, housing, belonging, and the strength of ties
          retained during college years. The teen survey reinforces the opening:
          49% of students can picture a future in the area and 29% say they would
          likely return. Goal 2’s workforce-pipeline strategies are designed to
          strengthen exactly these ties, complemented by in-migration of remote
          workers and entrepreneurs (Goal 4).
        </p>

        <h3>7.5 Program Alignment and Workforce Priorities</h3>
        <p>
          Based on the economic analysis and the employer-needs survey proposed
          under Goal 2, the highest-priority program alignment areas for CSC and
          WNCC are: healthcare (nursing, allied health, behavioral-health
          technician, telehealth support); precision agriculture and ag
          technology; skilled trades (welding, diesel, HVAC, electrical);
          broadband and technology infrastructure (fiber installation, network
          maintenance, IT support); and business development and succession.
          These align closely with the career interests teens themselves
          expressed — health science, agriculture, business, and trades —
          documented in Chapter 11.
        </p>

        <h3>7.6 Higher Education as Regional Economic Anchor</h3>
        <p>
          CSC and WNCC are not simply educational institutions serving
          individual students; they are regional economic anchors whose health
          is inseparable from the communities and workforce they serve.
          Declining enrollment at either translates directly into reduced
          employment, purchasing, civic capacity, and workforce output.
          Strengthening both institutions — their health, program-development
          capacity, and employer partnerships — is therefore a central concern
          of this strategy, not a peripheral one. They are not inputs to the
          strategy; they are the strategy.
        </p>

        <h3>7.7 Tracking Outcomes Over Time: NSWERS</h3>
        <p>
          This CEDS draws heavily on two point-in-time surveys, the 2026
          Community Survey and the 2023 Western Nebraska Teen Opportunities
          Survey, to establish where the region stands today. Both are valuable,
          but neither was designed to be repeated annually, and neither can, by
          itself, tell this CEDS or its Strategy Council whether the strategies
          in Goal 2 are actually changing outcomes over the five-year planning
          period rather than simply describing a single moment in time.
        </p>
        <p>
          The Nebraska Statewide Workforce &amp; Educational Reporting System
          (NSWERS) offers a partial answer to that gap. NSWERS is a
          state-maintained longitudinal data system (not a PADD program or a
          one-time survey) that links K-12, postsecondary, and workforce records
          across Nebraska’s Department of Education, all six community colleges,
          the Nebraska State College System, the University of Nebraska System,
          and the Nebraska Department of Labor. It tracks eight outcomes,
          several of which speak directly to the brain-drain question this
          chapter and Chapter 11 raise: high school graduation, college going,
          postsecondary persistence and graduation, time to employment,
          employment gap, industry placement, and — most relevant to this CEDS —
          employment location, which tracks where Nebraska’s graduates
          ultimately work. Nebraska’s Coordinating Commission for Postsecondary
          Education separately publishes County Continuation Rate dashboards
          showing college-going rates by county and by high school, updated
          annually back to the 2007–08 graduating cohort.
        </p>
        <p>
          This CEDS intends to use NSWERS’s published dashboards and briefs —
          available through insights.nswers.org — as an annually checkable,
          state-maintained reference point for whether CSC and WNCC graduates
          are increasingly choosing to work in Nebraska, and specifically in the
          Panhandle, over the planning period. Because NSWERS’s underlying
          student-level data is restricted by federal and state privacy law
          (FERPA and Nebraska wage-data statutes) and requires formal approval
          from the NSWERS Executive Council to access directly, this CEDS does
          not propose PADD obtain a custom regional extract in Year 1. It
          proposes the more modest and more readily achievable step of citing
          NSWERS’s existing public products as a recurring annual reference
          point, and pursuing a formal data-sharing conversation with NSWERS
          only if the Strategy Council determines region-specific data is needed
          beyond what is already published.
        </p>

        <h4>What NSWERS’s public data can and cannot resolve at the regional scale</h4>
        <p>
          NSWERS’s Employment Location dashboard reports a “talent retention
          rate” — the share of graduates from each Nebraska public
          postsecondary institution who are employed in Nebraska within two
          years of exiting — and reports it by institution, which means Chadron
          State College and Western Nebraska Community College each appear as
          named, separately trackable lines. This is the closest publicly
          available proxy for the Panhandle’s brain-drain question, and it is
          checkable annually. Two limits must be stated plainly alongside it.
          First, the dashboard measures retention in Nebraska as a whole, not
          retention in the Panhandle: a CSC or WNCC graduate who takes a job in
          Omaha or Lincoln counts as “retained” in this measure even though, for
          a regional strategy concerned with keeping talent in western Nebraska,
          that graduate has still left the region. Institution-level state
          retention is therefore a useful directional signal and a defensible
          starting baseline, but it is an upper bound on Panhandle retention,
          not a measure of it. Second, NSWERS’s employment data is drawn from
          Nebraska’s unemployment-insurance quarterly wage records, which cover
          an estimated 95 to 98 percent of Nebraska workers but exclude the
          self-employed, proprietors, unpaid family workers, and others whose
          employers are exempt from UI tax. In an agricultural region, those
          exclusions (farm proprietors and unpaid family farm labor in
          particular) fall disproportionately on exactly the kind of work the
          Panhandle economy is built on. A Panhandle graduate who returns home
          to work the family operation may appear in NSWERS as “unknown” rather
          than “retained,” which means the published retention figure likely
          understates true regional retention for an agricultural workforce more
          than it would statewide. This is not a reason to discard the measure;
          it is a reason to read it as a floor rather than a precise count, and
          to name the limit wherever the figure is cited.
        </p>

        <h4>The published research supports the strategic posture this CEDS already takes</h4>
        <p>
          NSWERS’s own Employment Location literature review reinforces, with
          external evidence, several of the strategic choices made elsewhere in
          this plan. It finds that two-year institutions retain graduates
          locally at substantially higher rates than four-year institutions —
          less-selective institutions keep roughly 78 percent of graduates
          in-state and 64 percent within the nearest metro area — which supports
          treating WNCC (a locally retentive two-year institution) and CSC (a
          four-year institution whose graduates disperse more widely) as playing
          distinct roles in the region’s workforce pipeline, as Chapter 7.2 and
          7.3 describe. It corroborates the Western Nebraska Teen Opportunities
          Survey’s finding that pay and career opportunity are the dominant
          reasons young people leave: the review reports that a majority of
          those who left Nebraska said job opportunities and pay were better
          where they moved, and that among rural Nebraskans, younger residents
          in particular are the most likely to be actively seeking better-paying
          work. Most relevant to this CEDS’s core strategy of keeping the door
          open for return rather than preventing departure, the review reports
          that a majority of people who left Nebraska said they would consider
          moving back if a suitable job were available — external, published
          validation of the return-migration posture this plan takes in Chapter
          7.4 and Chapter 11.
        </p>

        <h4>The data points to a specific, actionable retention lever: keep Panhandle students in-region for college</h4>
        <p>
          NSWERS’s published analysis finds consistently that graduates who are
          Nebraska residents are retained in Nebraska’s workforce at higher
          rates than the overall graduate pool — in NSWERS’s Employment Gap
          Brief, the resident retention rate exceeds the all-graduate rate for
          every high-demand occupation examined, by as much as seventeen
          percentage points. NSWERS draws the direct policy conclusion that
          increasing enrollment of Nebraska residents at in-state institutions,
          and giving them reasons to stay, is among the most effective available
          levers for retaining college-educated workers in the state. For the
          Panhandle, that finding sharpens into a concrete strategy: the more
          Panhandle young people who enroll at Chadron State and Western
          Nebraska Community College rather than leaving the region for college,
          the larger the pool the region can realistically retain — which is
          precisely what Goal 2’s workforce-pipeline and employer-linkage
          strategies are designed to support.
        </p>
        <p>
          Statewide, NSWERS’s talent retention rate has held remarkably steady —
          between 64 and 66 percent of degree-earning exiters from Nebraska
          public institutions every year from 2015 through 2023, reaching 66.1
          percent in the most recent (2023) cohort. This CEDS adopts that
          statewide figure as a stable reference baseline against which to read
          regional movement, while recognizing it is a state number, not a
          Panhandle one. At the institution level, the dashboard’s data carries
          two features worth naming honestly rather than glossing. First,
          Chadron State College’s blended retention rate appears low (roughly 41
          percent for the 2023 award-earning cohort), but that figure is driven
          down by out-of-state-origin students — young people from Wyoming,
          Colorado, and South Dakota who attend Chadron and then leave Nebraska
          after graduating. Among CSC’s Nebraska-resident students, retention
          is substantially higher, consistent with the statewide
          resident-versus-overall pattern above; the blended rate reflects
          Chadron’s role as a border-region institution serving a multi-state
          student body, not a failure to retain Nebraska’s own. Western Nebraska
          Community College, by contrast, draws an almost entirely in-state
          student body, as a community college would. Second, both institutions
          enroll small enough cohorts that a single year’s rate is statistically
          fragile — WNCC’s measured cohort numbers in the dozens, and NSWERS
          suppresses several cells to protect privacy — so this CEDS treats the
          institution-level figures as directional context, and the multi-year
          statewide trend as the more reliable baseline. This is the same
          small-population caution Appendix D applies to county income data,
          applied here to institutional workforce data: read the trend, not the
          single fragile point.
        </p>
        <p>
          One further coverage limit compounds the small-sample caution and runs
          in a consistent direction. Because retention is measured through
          Nebraska unemployment-insurance wage records — which exclude the
          self-employed, proprietors, and unpaid family workers — a Panhandle
          graduate who returns home to a family farm or ranch, or to
          self-employment, is recorded as “unknown” rather than “retained.” In
          an agricultural region, this means the true regional retention of CSC
          and WNCC graduates is very likely higher than these figures show. The
          direction of the measurement error is knowable even though its size is
          not, which is another reason this CEDS reads these numbers as a floor.
        </p>

        <h4>A gap this CEDS will not paper over</h4>
        <p>
          NSWERS’s outcome framework is built around two credentialed on-ramps —
          a regular high school diploma or, separately, a GED/high school
          equivalency certificate — and tracks what happens to students after
          either pathway: college going, persistence, employment location, and
          the rest. Students who leave the K-12 system without completing either
          credential are captured once, as non-completers, in the high school
          graduation outcome itself; Nebraska’s K-12 reporting system requires
          districts to report dropouts by name and grade level. But once a
          student exits without a diploma or a GED, this CEDS is not aware of a
          NSWERS outcome that follows that same student forward into
          college-going, employment location, or any of the system’s other
          measures. A young person who leaves school without a credential and
          later works informally, works in agriculture or another sector with
          limited unemployment-insurance wage coverage, or leaves Nebraska
          entirely, may not surface again in any NSWERS outcome after the initial
          non-completion is recorded. This matters for the Panhandle
          specifically: a regional strategy concerned with whether young people
          stay, leave, or return cannot responsibly claim a complete picture of
          that question from a data system that may lose track of the very young
          people most disconnected from school and formal work — arguably the
          population this CEDS most needs to understand. This CEDS will pursue
          two parallel paths to address that gap rather than wait for a single
          solution: First, this CEDS will request a direct conversation with
          NSWERS and the Nebraska Department of Education about whether, and
          how, non-completers are tracked beyond the initial dropout report, and
          will revise this chapter once that question has an answer rather than
          assume either a gap or a solution. Second, recognizing that some young
          people will remain genuinely outside any administrative data system,
          this CEDS treats local, qualitative engagement (through schools,
          juvenile justice and human-services partners, and community
          organizations working directly with disconnected youth) as a necessary
          complement to NSWERS, not an inferior substitute for it. The Strategy
          Council should consider whether a future engagement cycle should
          include this population directly, since neither the 2026 community
          survey nor the 2023 teen survey was designed to reach young people no
          longer enrolled in a participating school district.
        </p>

        <h4>A note on what this can and cannot tell us</h4>
        <p>
          This CEDS is being explicit, here and throughout, about the limits of
          the evidence it relies on. If NSWERS data shows Panhandle-area
          employment location or postsecondary persistence improving over the
          planning period, this CEDS will not claim that improvement was caused
          by PADD’s strategies — the state’s economy, the broader labor market,
          energy prices, federal policy, public health events, and countless
          other forces outside this plan’s control all move these same
          indicators, often more powerfully than any single five-year regional
          strategy could. The value of a consistent, externally maintained
          measurement source like NSWERS is not that it isolates PADD’s effect
          from everything else (no single five-year regional plan could credibly
          claim that) but that it gives the Strategy Council the same yardstick,
          applied the same way, year after year, for the population it does
          reach. Tracking the trend honestly, with both its confounding
          variables and its coverage limits named rather than hidden, is more
          useful to the region than a more confident-sounding measurement built
          on shakier ground.
        </p>
        <p className="source-note">
          Source note: NSWERS program description and outcome framework from
          nswers.org and insights.nswers.org (Nebraska Statewide Workforce &amp;
          Educational Reporting System); talent retention rate, institution-level
          and student-origin breakdowns, statewide longitudinal trend
          (2015–2023), and unemployment-insurance wage-coverage limitations from
          the NSWERS Employment Location dashboard and its downloadable data
          (Degree-seeking Exiter With Award cohort, 2023; data generated June
          2026), insights.nswers.org/outcomes/employment-location;
          Nebraska-resident-versus-overall retention finding from the NSWERS
          Employment Gap Brief (2025), insights.nswers.org; brain-drain research
          findings (institutional retention patterns, reasons for leaving,
          return-migration intent) from the NSWERS Employment Location Literature
          Review (2025), insights.nswers.org; high school graduation outcome
          definition, including treatment of GED/equivalency completion, from
          insights.nswers.org/outcomes/high-school-graduation; dropout reporting
          requirements from the Nebraska Department of Education’s Nebraska
          Student and Staff Record System (NSSRS) dropout reporting guidance;
          county college-going dashboards from Nebraska’s Coordinating
          Commission for Postsecondary Education, ccpe.nebraska.gov.
        </p>
      </div>
    </div>
  );
}
